import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";

type ManagePlanQuantityStepperProps = {
  value: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  decrementTooltip?: string;
  onChange: (value: number) => void;
};

export function ManagePlanQuantityStepper({
  value,
  min = 1,
  max = 99,
  disabled = false,
  decrementTooltip,
  onChange,
}: ManagePlanQuantityStepperProps) {
  const decrementDisabled = disabled || value <= min;
  const incrementDisabled = disabled || value >= max;
  const decrementRef = useRef<HTMLButtonElement>(null);
  const [tooltipPos, setTooltipPos] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const showTooltip = useCallback(() => {
    if (!decrementDisabled || !decrementTooltip || !decrementRef.current)
      return;
    const rect = decrementRef.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  }, [decrementDisabled, decrementTooltip]);

  const hideTooltip = useCallback(() => setTooltipPos(null), []);

  return (
    <div className="flex w-full items-center gap-3">
      <div className="shrink-0">
        <button
          ref={decrementRef}
          type="button"
          data-test="stepper-decrement"
          disabled={decrementDisabled}
          onClick={() => onChange(Math.max(min, value - 1))}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-[6px] border-0 bg-white text-[#1f2633] shadow-[0_0_0_1px_rgba(31,38,51,0.12),0_1px_3px_rgba(31,38,51,0.12)] transition hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
          >
            <path d="M3.5 8h9" />
          </svg>
        </button>
        {tooltipPos &&
          decrementTooltip &&
          createPortal(
            <div
              className="pointer-events-none fixed z-[9999] w-max max-w-[220px] -translate-x-1/2 -translate-y-full rounded-md bg-[#1f2633] px-2.5 py-1.5 text-[11px] leading-4 text-white shadow-lg"
              style={{ top: tooltipPos.top, left: tooltipPos.left }}
            >
              {decrementTooltip}
              <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-[#1f2633]" />
            </div>,
            document.body,
          )}
      </div>
      <div
        data-test="stepper-value"
        className="flex h-8 flex-1 items-center justify-center rounded-[6px] bg-white px-3 text-xs leading-4 text-[#454d5f] shadow-[0_0_0_1px_rgba(31,38,51,0.12)]"
      >
        {value}
      </div>
      <button
        type="button"
        data-test="stepper-increment"
        disabled={incrementDisabled}
        onClick={() => onChange(Math.min(max, value + 1))}
        className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-[6px] border-0 bg-white text-[#1f2633] shadow-[0_0_0_1px_rgba(31,38,51,0.12),0_1px_3px_rgba(31,38,51,0.12)] transition hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-50"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
        >
          <path d="M8 3.5v9M3.5 8h9" />
        </svg>
      </button>
    </div>
  );
}
